import React, { CSSProperties } from 'react';
import ReactTooltip from 'react-tooltip';
import { AppStyled, TitleBarStyled } from './App.styles';
import { appHistory, AppHistoryEntry, AppHistoryState } from './AppHistory';
import { FilterDef, ImgCollection, ImageSrc, LanguageKeys, OpenFileHandle, OptionsType, SaveFileHandle, Settings } from './CommonTypes';
import { CrispImg, DragAndDrop, FAButton, Overlayable, SidePanel, SidePanelContainer, Slider, ZoomableContainer, clamp, throttle } from './components';
import { IToolWrapped, MeasureToolCreator, MeasureToolWrapped, ToolWrapper } from './components/measure_tools/Creator';
import { About, ImageChooser } from './components/measure_ui';
import { SaveDialog } from './components/measure_ui/SaveDialog';
import globals from './globals';
import { LicenseList } from './license/Licenses';
import { localization } from './localization';
import { tiffToPng } from './tiff_utils';
import { extractPhenomExif, getInfoFromExif, getInfoFromName, getInfoOrDefault, getNameWithoutExt, imgName, nonNull } from './utils';

// app props
export type IAppProps = {
  props: {
    collection: ImgCollection;
    filters: FilterDef[];
    licenses: LicenseList;
    validExtensions: string[];
  };
  utils: {
    getSrcString: (src: string) => string;
    getThumbString: (src: string) => Promise<string>;
    getDefaultOpts: () => OptionsType;
    openDialog: (title: string, validExtensions: string[], allowMultiple: boolean, lang: LanguageKeys) => Promise<OpenFileHandle[]>;
    saveDialog: (title: string, suggestedName: string, lang: LanguageKeys) => Promise<SaveFileHandle | undefined>;
    openLink: (link: string) => void;
    fileToSrc: (file: OpenFileHandle) => Promise<ImageSrc>;
    blobToSrc: (blob: Blob, name: string) => Promise<ImageSrc>;
  };
};

// app state
export interface IAppState {
  unit: string;
  lengthScale: number;
  overlayScale: number;
  zoomVal: number;
  panelCollapsed: boolean;
  openPanel: number;
  interacting?: number;
  measurements: MeasureToolWrapped[];
  renderAreaStyle: CSSProperties;
  imageSrc?: ImageSrc;
  imageInfo?: {
    width: number;
    height: number;
    pixel: number;
  };

  allowEdit: boolean;
  allowExternalSrc: boolean;

  filterVals: number[];
  showSave?: boolean;
  showAbout?: boolean;
  showImageChooser?: number | string;
  acceptDrop: boolean;
  settings: Settings;

  saveOverlayScale: number;
  showQuickInstruction: boolean;

  justNavigated?: boolean;
}

export default class App extends React.Component<IAppProps, IAppState> {
  private zoomRef = React.createRef<ZoomableContainer>();
  private renderAreaRef = React.createRef<SVGSVGElement>();
  private uiAreaRef = React.createRef<HTMLDivElement>();
  private toolsAreaRef = React.createRef<HTMLDivElement>();
  private saveRenderRef = React.createRef<SVGSVGElement>();
  private measurementRefs: (IToolWrapped | null)[] = [];

  private getStartingState = (opts?: OptionsType): IAppState => {
    opts = { ...this.props.utils.getDefaultOpts(), ...opts };
    const lang = opts.lang || 'en';
    let imageSrc: ImageSrc | undefined = undefined;
    if (opts.src) {
      const name = opts.name || (opts.src.startsWith('http') ? opts.src : getNameWithoutExt(opts.src));
      imageSrc = { src: opts.src, name: name, unit: opts.unit, pixel: opts.pixel, width: opts.width, height: opts.height };
    }
   

    return {
      unit: opts.unit || localization[lang].measurements.pixelUnit,
      lengthScale: 1,
      overlayScale: 0,
      zoomVal: 0,
      openPanel: 1,
      panelCollapsed: opts.collapsed || false,
      measurements: [],
      renderAreaStyle: {},
      imageSrc: imageSrc,

      allowEdit: opts.allowEdit || false,
      allowExternalSrc: opts.allowExternalSrc || false,

      filterVals: this.props.props.filters.map(fd => fd.defaultValue),
      showImageChooser: opts.openCat ? opts.openCat : (imageSrc ? undefined : -1),
      acceptDrop: false,
      settings: {
        lang,
        transitionDuration: 0.4
      },
      saveOverlayScale: 0,
      showQuickInstruction: true
    };
  }

  constructor(props: IAppProps) {
    super(props);
    
    // hide imageChooser here to avoid flashing if it isn't shown on history load
    this.state = { ...this.getStartingState(), showImageChooser: undefined };
  }

  private onContextMenu = (e: MouseEvent) => {
    e.preventDefault();
  };

  private onZoomValChanged = (newVal: number) => {
    this.setState({ zoomVal: newVal });
  };


  // alert on error, but try to convert tiffs first
  private onImageError = async () => {
    try {
      let src = { ...this.state.imageSrc! };
      const name = imgName(src, this.state.settings.lang);
      const srcTiff = src.src.endsWith('.tif') || src.src.endsWith('.tiff');
      const nameTiff = name.endsWith('tif') || name.endsWith('.tiff');
      if (srcTiff || nameTiff) {
        const tiffBlob = await (await fetch(src.src, { cache: 'only-if-cached', mode: 'same-origin' })).blob()
        const pngBlob = await tiffToPng(tiffBlob);
        if (pngBlob) {
          const info = await getInfoOrDefault(srcTiff ? src.src : name, tiffBlob, this.state.settings.lang);
          src.src = (await this.props.utils.blobToSrc(pngBlob, getNameWithoutExt(src.src) + '.png')).src;
          src.pixel = src.pixel || info.pixel;
          src.unit = src.unit || info.unit;
          this.setState({ imageSrc: src });
          return;
        }
      }
    } catch(err) {
      console.log(`Failed fallbacks: ${err}`);
    }
    alert(localization[this.state.settings.lang].loadingError);
    this.setState({ imageSrc: undefined, showImageChooser: -1 });
  };

  private onImageLoaded = async (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    if (this.state.justNavigated === true) {
      super.setState({ justNavigated: false });
      return;
    }
    const img = e.currentTarget;

    let { unit, pixel, width, height, ...imgSrc } = this.state.imageSrc!;
    const { filters } = this.props.props;

    let lengthScale = pixel
      || (width ? width / img.width : undefined)
      || (height ? height / img.height : undefined);
    
      // try to read from image if its not in source
    if (!unit || !lengthScale) {
      try {
        let info = getInfoFromName(this.state.imageSrc!.src);
        if (!info) {
          // we just loaded it, it should be cached
          const imgText = await (await fetch(this.state.imageSrc!.src, { cache: 'only-if-cached', mode: 'same-origin' })).text();
          const exif = extractPhenomExif(imgText);
          if (exif)
            info = getInfoFromExif(exif);
        }
        unit = info?.unit;
        lengthScale = info?.pixel;
      } catch(err) {
        console.log(`Error reading info from image: ${err}`);
      } 
    }
    
    document.title = 'strucTEM Measure: ' + imgName(imgSrc, this.state.settings.lang);

    const zoomCont = this.zoomRef.current;
    this.setState({
      imageInfo: { width: img.width, height: img.height, pixel: 1 },
      imageSrc: { ...imgSrc, unit: unit, pixel: lengthScale },
      lengthScale: lengthScale || 1,
      unit: unit || localization[this.state.settings.lang].measurements.pixelUnit,
      filterVals: filters.map(fd => fd.defaultValue),
      measurements: [],
      zoomVal: zoomCont ? zoomCont.getFittingZoom() : 0
    });
  };

  private onNewMeasurement = (tool?: MeasureToolWrapped) => {
    if (tool) {
      this.setState(prev => ({
        interacting: undefined,
        measurements: [...prev.measurements, tool]
      }));
    } else {
      this.setState({ interacting: undefined });
    }
  };

  private onDeleteMeasurement = (index: number) => {
    this.setState(prev => {
      let measurements = [...prev.measurements];
      measurements.splice(index, 1);
      return { measurements, interacting: undefined };
    }, ReactTooltip.hide );
  };

  private onChangeInteracting = (interacting: boolean, measIndex: number) => {
    this.setState({ showQuickInstruction: false, interacting: interacting ? measIndex : undefined });
  };

  private onLengthScaleChange = (newScale: number) => this.setState({ lengthScale: newScale });

  private onUnitChange = (newUnit: string) => this.setState({ unit: newUnit });

  private onSettingsChanged = (newSettings: Settings) => {
    if (newSettings.lang !== this.state.settings.lang && this.state.imageSrc)
      document.title = `strucTEM Measure: ${ imgName(this.state.imageSrc, newSettings.lang)}`;
    this.setState({ settings: newSettings });
  };

  private onSettingsReset = () => {
    const opts = this.props.utils.getDefaultOpts();
    this.onSettingsChanged({ transitionDuration: 0.4, lang: opts.lang || 'en' });
  };

  private openImage = (src: ImageSrc) => {
    appHistory.save({}, this.state.imageSrc !== undefined);
    this.setState({
      imageSrc: src,
      showImageChooser: undefined, acceptDrop: false, showAbout: false, showSave: false
    });
  };

  private openBlob = async (blob: Blob, name: string) => {
    const src = await this.props.utils.blobToSrc(blob, name);
    this.openImage(src);
  };

  private onFilePicked = async (file: OpenFileHandle) => {
    const src = await this.props.utils.fileToSrc(file);
    this.openImage(src);
  };

  private onOpenImg = async (cat: number, external?: boolean) => {
    if (external && this.state.allowExternalSrc) {
      const fileHandles = await this.props.utils.openDialog(
        `strucTEM Measure: ${localization[this.state.settings.lang].openDialog.openImage}`,
        this.props.props.validExtensions,
        false, this.state.settings.lang
      );
      if (fileHandles.length > 0) {
        await this.onFilePicked(fileHandles[0])
      }
    } else {
      this.setState({ showImageChooser: cat });
    }
  };

  private loadHistoryEntry = (entry: AppHistoryEntry) => {
    if (entry.data) {
      const oldState = { ...this.state };
      try {
        const { measurementsData, windowTitle, scrollOffset, ...restState } = entry.data;
        const measurements = measurementsData.map(m => ToolWrapper({ name: m.name, data: m.data, hidden: m.hidden }, m.tool));
        const newState: IAppState = {
          ...restState, measurements,
          justNavigated: true, showSave: false, showAbout: false,
          showImageChooser: restState.imageSrc ? undefined : -1,
          showQuickInstruction: this.state.showQuickInstruction && measurementsData.length === 0,
          panelCollapsed: this.state.panelCollapsed
        };
        document.title = windowTitle;
        super.setState(newState, () => {
          this.zoomRef.current?.scrollTo(scrollOffset, false);
          // sometimes the scroll didn't work
          setTimeout(() => this.zoomRef.current?.scrollTo(scrollOffset, false), 100);
        });
      } catch (err) {
        console.log(`Failed to load history state: ${err}`);
        this.setState(oldState);
      }
    } else {
      this.setState({ ...this.getStartingState(entry.opts), justNavigated: false });
    }
  };

  private saveHistory = throttle(() => {
    const scrollOffset = this.zoomRef.current?.getScroll() || { x: 0, y: 0 };
    // extranct unneeded components from state
    const {
      showAbout, showImageChooser, showSave, measurements,
      interacting, justNavigated, showQuickInstruction, panelCollapsed,
      ...restState
    } = this.state;
    const newHistState: AppHistoryState = {
      ...restState,
      windowTitle: document.title,
      measurementsData: this.measurementRefs.filter(nonNull).map(ref => ref.serialize()),
      scrollOffset
    };
    try {
      appHistory.save({ data: newHistState }, false);
    } catch(err) {
      console.warn(`Failed to save state: ${err}`);
    }
  }, 250);

  // safe state to history on any change
  setState<K extends keyof IAppState>(
    state: ((prevState: Readonly<IAppState>, props: Readonly<IAppProps>) => (Pick<IAppState, K> | IAppState | null)) | (Pick<IAppState, K> | IAppState | null),
    callback?: () => void
  ): void {
    super.setState(state, () => {
      this.saveHistory();
      if (callback) callback();
    });
  }

  componentDidMount = () => {
    document.addEventListener('contextmenu', this.onContextMenu);
    appHistory.addRestoreListener(this.loadHistoryEntry);

    // load initial state (either from opts on first opening or from previous state on back/forward/refresh)
    // this.loadHistoryEntry(appHistory.getCurrent());
  };

  componentWillUnmount = () => {
    document.removeEventListener('contextmenu', this.onContextMenu);
    appHistory.removeRestoreListener(this.loadHistoryEntry);
  };

  componentDidUpdate = (prevProps: IAppProps, prevState: IAppState) => {
    // needed to refresh the render refs
    if (prevState.showSave !== this.state.showSave && this.state.showSave === true) {
      this.forceUpdate();
    }
    if (prevProps !== this.props) {
      ReactTooltip.rebuild();
    }
  };

  public render() {
    const {
      allowEdit, allowExternalSrc, imageInfo, imageSrc, settings,
      showQuickInstruction, lengthScale, filterVals, renderAreaStyle,
      showAbout, showImageChooser, showSave, overlayScale, acceptDrop,
      saveOverlayScale, measurements, zoomVal, interacting, unit, panelCollapsed
    } = this.state;

    const { filters, collection, licenses, validExtensions } = this.props.props;
    const { openLink, getSrcString, saveDialog } = this.props.utils;

    const logoPath = this.props.props.collection[settings.lang]?.logo || localization[settings.lang].measureLogo;
    const homePage = this.props.props.collection[settings.lang]?.homepage || localization[settings.lang].measureHome;

    const actualOverlayScale = Math.exp(overlayScale -zoomVal);
    const MeasurementElems = measurements.map(({ tool, key }, i) => {
      return React.createElement(tool, {
        index: i,
        unit: unit,
        onUnitChanged: allowEdit ? this.onUnitChange : undefined,
        lengthScale: lengthScale,
        onLengthScaleChanged: allowEdit ? this.onLengthScaleChange : undefined,
        settings: settings,
        UIPortals: [{ portal: this.uiAreaRef.current }],
        RenderPortals: [{
          portal: this.renderAreaRef.current,
          overlayScale: actualOverlayScale,
          requestStyle: newStyle => this.setState({ renderAreaStyle: newStyle })
        }, {
          portal: this.saveRenderRef.current,
          overlayScale: Math.exp(saveOverlayScale)
        }],
        onChangeInteracting: interacting => this.onChangeInteracting(interacting, i),
        onDelete: () => this.onDeleteMeasurement(i),
        allowInteraction: interacting === undefined || interacting === i,
        ref: elem => this.measurementRefs[i] = elem,
        key
      });
    });

    const iInfo = imageInfo || { pixel: 0, width: 0, height: 0 };
    const Panel = <SidePanel logoPath={logoPath} homePage={ homePage }
      uiAreaRef={this.uiAreaRef} toolsAreaRef={this.toolsAreaRef}
      overlayScale={overlayScale}
      onOverlayScaleChanged={newOverlayScale => this.setState({ overlayScale: newOverlayScale })}
      onSaveClicked={() => this.setState({ saveOverlayScale: clamp(overlayScale - zoomVal, -1, 2), showSave: true })}
      onDeleteAllClicked={() => this.setState({ measurements: [] })}
      filters={ filters }
      filterValues={filterVals}
      onFilterChanged={newFilterVals => this.setState({ filterVals: newFilterVals })}
      settings={settings }
      settingProps={{
        collection,
        allowExternalSrc: allowExternalSrc,
        onSettingsChanged: this.onSettingsChanged,
        onSettingsReset: this.onSettingsReset,
        onOpenImage: this.onOpenImg
      }}
      onShowAbaout= {() => this.setState({ showAbout: true })}
      openLink={ openLink }
      imageInfo={{
        ...iInfo,
        scale: lengthScale,
        onScaleChanged: allowEdit ? this.onLengthScaleChange : undefined,
        unit: unit,
        onUnitChanged: allowEdit ? this.onUnitChange : undefined
      }} />;

    const filterStr = filters.map((fd, i) => fd.transform(filterVals[i])).join(' ');
    const MainAreaRender = imageSrc ? <>
      <ZoomableContainer ref={this.zoomRef} zoomProps={{ value: zoomVal, min: -5, max: 5, step: 0.25 }}
        onChange={this.onZoomValChanged} onScrollChange={this.saveHistory}>
        <Overlayable base={
          <CrispImg src={getSrcString(imageSrc.src)} onLoad={this.onImageLoaded} style={{ filter: filterStr }} onError={this.onImageError} />
        }>
          <svg id='RenderArea' ref={this.renderAreaRef} style={renderAreaStyle} />
          <MeasureToolCreator
            allowCreation={interacting === -1 || interacting === undefined}
            lengthScale={lengthScale}
            unit={unit}
            settings={settings}
            creationIconsPortal={this.toolsAreaRef.current}
            renderPortal={{ portal: this.renderAreaRef.current, overlayScale: actualOverlayScale }}
            onCreationStart={() => this.onChangeInteracting(true, -1)}
            onCreationDone={this.onNewMeasurement}
          />
        </Overlayable>
      </ZoomableContainer>
      {showQuickInstruction ? (<>
        <span id='QuickOpen'>
          {localization[settings.lang].quickOpen}
          <span>&#8624;</span>
        </span>
        <span id='QuickInstructions'>{localization[settings.lang].quickInstruction}</span>
      </>) : null}
    </> : null;

    const MainAreaOverlay = (
      null
    );

    const MainArea = (
      <Overlayable sizeToParent base={MainAreaRender}>
        { MainAreaOverlay}
      </Overlayable>
    )
    
    const zoomProps = globals.zoom;
    let title = '';
    if (imageSrc) {
      title = imgName(imageSrc, settings.lang) + (imageSrc.magnification ? ` [${imageSrc.magnification}x]` : '');
    };
    const isOrig = (s: IAppState) => s.zoomVal === zoomProps.default && this.zoomRef.current ? true : false;
    const TitleBar = (
      <TitleBarStyled interacting={interacting !== undefined}>
        <span>{title}</span>
        <FAButton icon={isOrig(this.state) ? 'compress' : 'expand'} size={35}
          data-tip={localization[settings.lang].tooltips.zoomToggle}
          onClick={() => this.setState(prev => ({ zoomVal: isOrig(prev) ? this.zoomRef.current!.getFittingZoom() : zoomProps.default }))} />
        <FAButton icon='minus' size={35} onClick={() => this.setState(prev => ({ zoomVal: prev.zoomVal - zoomProps.mouseStep }))}
          data-tip={localization[settings.lang].tooltips.zoomOut} />
        <Slider value={zoomVal} min={zoomProps.min} max={zoomProps.max} step={zoomProps.sliderStep}
          onChange={e => this.setState({ zoomVal: parseFloat(e.currentTarget.value) })}
          data-tip={ localization[settings.lang].tooltips.zoomChange }/>
        <FAButton icon='plus' size={35} onClick={() => this.setState(prev => ({ zoomVal: prev.zoomVal + zoomProps.mouseStep }))} 
          data-tip={localization[settings.lang].tooltips.zoomIn}/>
        <FAButton icon='images' size={35} onClick={() => this.setState({ showImageChooser: -1 })}
          data-tip={localization[settings.lang].tooltips.openImage}/>
      </TitleBarStyled>
    );

    const AppOverlay = showImageChooser !== undefined && collection.images.length > 0 ? (
      <ImageChooser props={{ collection, validExtensions }} allowExternalSrc={allowExternalSrc}
        logoPath={ logoPath }
        utils={this.props.utils} settings={settings} currentImage={imageSrc}
        initialCat={showImageChooser >= 0 ? showImageChooser : undefined}
        requestClose={() => this.setState({ showImageChooser: undefined })}
        imageClicked={this.openImage} filePicked={ this.onFilePicked } />
    ) : (showSave && imageInfo && imageSrc) ? (
        <SaveDialog getSrcString={ getSrcString } settings={settings} imageSrc={imageSrc}
        renderPortalRef={this.saveRenderRef}
        overlayScaleVal={saveOverlayScale}
        onOverlayScaleValChanged={newVal => this.setState({ saveOverlayScale: newVal })}
        imageInfo={{ ...imageInfo, unit: unit, scale: lengthScale }}
          filterString={filterStr}
          saveDialog={ saveDialog }
        onFinish={() => this.setState({ showSave: undefined })}
        measurementRefs={this.measurementRefs} />
    ) : showAbout ? (
          <About logoPath={ logoPath } licenses={ licenses } settings={settings} requestClose={() => this.setState({ showAbout: undefined })} openLink={openLink} />
    ) : null;
    
    const DropAccept = (acceptDrop && allowExternalSrc) ? (
      <DragAndDrop allowMultiple={false} allowedFileTypes={['image']}
        onDone={() => this.setState({ acceptDrop: false })}
        onDropFiles={files => this.openBlob(files[0], files[0].name)}
        onDropOther={this.openBlob }/>
    ) : null;
    
    const AppBase = (
      <SidePanelContainer panelPos='left' transitionDuration={settings.transitionDuration} filter={AppOverlay ? 'blur(5px)' : undefined}
        panelWidth={400} panelOpen={!panelCollapsed} panel={Panel} titleBar={TitleBar}
        onChange={interacting !== undefined && panelCollapsed ? undefined :
          () => this.setState(prev => ({ panelCollapsed: !prev.panelCollapsed }))}>
        { MainArea}
        { MeasurementElems }
      </SidePanelContainer>
    );
    
    return (
      <AppStyled id='App' onDragEnter={allowExternalSrc ? () => this.setState({ acceptDrop: true }) : undefined}>
        <Overlayable sizeToParent base={AppBase}>
          {AppOverlay}
          {DropAccept}
        </Overlayable>
        <ReactTooltip type='dark' effect='solid' />
      </AppStyled>
    );
  }
}
